import { addNotification } from "portal/state/notifications";
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { AtkConfigurationForm } from "./atk-configuration/RigConfigurationForms";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import {
  editingSupported,
  removeImplement,
  removeTractor,
  RigDetailsComputedFormState,
  RigDetailsFormState,
  saveFormStateToRig,
} from "./FormState";
import { EquipmentLibraryCard } from "../../../components/atk/EquipmentLibraryCard";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { getImplementLibraryCardData } from "portal/components/atk/ImplementLibraryCardData";
import {
  getNewRigPath,
  getRobotPath,
  getSwitchRigPath,
} from "portal/utils/routing";
import { getTractorLibraryCardData } from "portal/components/atk/TractorLibraryCardData";
import { Header } from "portal/components/header/Header";
import { Page } from "portal/components/Page";
import { portalApi } from "portal/state/portalApi";
import { RigDetailsHeader } from "portal/views/autotractor/rig-details/RigDetailsHeader";
import {
  RigDetailsPageLocationState,
  RigDetailsPageView,
  RigDetailsSwitchViewCallback,
} from "../RigDetailsPage";
import { RigPerformanceDetails } from "portal/views/autotractor/rig-details/RigPerformanceDetails";
import { RigStatus } from "portal/views/autotractor/rig-details/RigStatus";
import { RigValidationContext } from "../RigValidationContext";
import { RobotSummaryResponse } from "protos/portal/robots";
import { titleCase, valueOrLoading } from "portal/utils/strings";
import { typedPath } from "typed-path";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActiveIcon from "@mui/icons-material/CheckCircleOutlined";
import AddIcon from "@mui/icons-material/AddCircleOutlined";
import ConnectionsIcon from "@mui/icons-material/Link";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import React, { ReactElement, ReactNode, useContext } from "react";
import RemoveIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/SaveOutlined";

const S = typedPath<RigDetailsFormState>();

interface GhostPartProps {
  text: string;
}

/**
 * GhostPart represents a part on the rig diagram that is not populated and
 * cannot be populated because the rig shape is not editable.
 */
const GhostPart: React.FC<GhostPartProps> = ({ text }) => {
  return (
    <Button
      className="border-2 text-gray-400 border-gray-400 border-solid rounded-xl w-80 text-center"
      disabled={true}
    >
      {text}
    </Button>
  );
};

interface AddPartButtonProps {
  text: string;
  onClick: () => void;
}

const AddPartButton: React.FC<AddPartButtonProps> = ({ text, onClick }) => {
  return (
    <Button
      className="border-2 text-gray-400 border-gray-400 border-dashed rounded-xl w-80"
      startIcon={<AddIcon className="text-4xl m-auto" />}
      onClick={onClick}
    >
      {text}
    </Button>
  );
};

interface RigDiagramProps {
  canEdit: boolean;
  switchToView: RigDetailsSwitchViewCallback;
}

export const RigDiagram: React.FC<RigDiagramProps> = ({
  canEdit,
  switchToView,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<RigDetailsFormState>();
  const computedState = useContext(RigDetailsComputedFormState);
  const { values } = formik;

  let tractorComponent: ReactNode;

  if (values.tractor) {
    tractorComponent = (
      <EquipmentLibraryCard
        variant="small"
        className="w-80"
        data={getTractorLibraryCardData(t, values.tractor)}
        ActionButtonIcon={canEdit ? RemoveIcon : undefined}
        onActionButtonClick={() => removeTractor(formik)}
      />
    );
  } else if (canEdit) {
    tractorComponent = (
      <AddPartButton
        text={"Choose tractor"}
        onClick={() => switchToView("tractor-library")}
      />
    );
  } else {
    // can't edit and no tractor assigned.  this shouldn't happen, but do
    // something reasonable if it did anyway.
    tractorComponent = <GhostPart text={"No tractor"} />;
  }

  return (
    <div className="flex flex-row gap-4 h-36">
      {/* tractor "group" */}
      <div className="flex flex-col gap-4 h-full">
        <Typography>
          {titleCase(t("models.equipment.tractor", { count: 1 }))}
        </Typography>
        <div className="flex flex-row gap-4 h-full">
          {tractorComponent}
          <Button
            variant="contained"
            className="rounded-xl w-min"
            onClick={() => switchToView("connections")}
          >
            <div className="flex flex-col items-center">
              <ConnectionsIcon />
              <div>{t("views.autotractor.equipment.connections")}</div>
            </div>
          </Button>
        </div>
      </div>
      {/* implement "group" */}
      <div className="flex flex-col gap-4 h-full">
        <Typography className="col-start-3 row-start-1">
          {titleCase(
            t("models.equipment.implement", { count: values.implements.length })
          )}
        </Typography>
        <div className="flex flex-row gap-4 h-full">
          {values.implements.map((implement) => {
            const libraryCardData = getImplementLibraryCardData(t, implement);

            return (
              <EquipmentLibraryCard
                key={implement.id}
                className="w-80"
                data={libraryCardData}
                variant="small"
                ActionButtonIcon={canEdit ? RemoveIcon : undefined}
                onActionButtonClick={() =>
                  removeImplement(formik, computedState, implement.id)
                }
              />
            );
          })}
          {!canEdit && values.implements.length === 0 && (
            <GhostPart text={"no implements"} />
          )}
          {canEdit && (
            <AddPartButton
              text={"add implement"}
              onClick={() => switchToView("implement-library")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const StatusAndConfiguration: React.FC<{ readOnly: boolean }> = ({
  readOnly,
}) => {
  return (
    <div className="columns-2 flex flex-row gap-4 w-full">
      <div className="flex flex-col gap-4 w-full">
        <RigStatus />
        <RigPerformanceDetails />
      </div>
      <div className="flex flex-col w-full">
        <AtkConfigurationForm readOnly={readOnly} />
      </div>
    </div>
  );
};

interface LibraryComponentProps<T> {
  isLoading: boolean;
  entities: T[];
  dataPath: string;
  renderCard: (entity: T, arrayHelpers: FieldArrayRenderProps) => ReactElement;
  close: () => void;
}

const LibraryComponent = <T,>({
  isLoading,
  entities,
  dataPath,
  renderCard,
  close,
}: LibraryComponentProps<T>): ReturnType<
  React.FC<LibraryComponentProps<T>>
> => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-row gap-4 w-full">
        {/* <SearchField className="flex-1" value="" onChange={() => {}} /> */}
        <div className="flex-1" />
        <Button variant="contained" color="secondary" onClick={close}>
          {t("utils.actions.cancel")}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex flex-row gap-4 items-center justify-center">
          <CircularProgress variant="indeterminate" />
          <Typography variant="h5">{t("utils.descriptors.loading")}</Typography>
        </div>
      ) : (
        <div className="grid grid-cols-3 lg:grid-cols-4 grid-flow-row gap-4 justify-start">
          <FieldArray name={dataPath}>
            {(arrayHelpers) =>
              entities.map((entity) => renderCard(entity, arrayHelpers))
            }
          </FieldArray>
        </div>
      )}
    </div>
  );
};

const TractorLibrary: React.FC<RigPartsAndConfigurationProps> = ({
  switchToView,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<RigDetailsFormState>();
  const { data: searchResult, isFetching: tractorsFetching } = useQueryPopups(
    atkEquipmentApi.endpoints.searchTractors.useQuery({
      customerId: values.customerId,
    })
  );
  const { setFieldValue, setFieldTouched } =
    useFormikContext<RigDetailsFormState>();

  return (
    <LibraryComponent
      isLoading={tractorsFetching}
      entities={searchResult?.tractors ?? []}
      dataPath={S.tractor.$path}
      renderCard={(tractor, _) => (
        <EquipmentLibraryCard
          key={tractor.id}
          data={getTractorLibraryCardData(t, tractor)}
          variant="small"
          onCardClick={() => {
            setFieldValue(S.tractor.$path, tractor);
            setFieldTouched(S.tractor.$path);
            switchToView("status-and-config");
          }}
        />
      )}
      close={() => switchToView("status-and-config")}
    />
  );
};

const ImplementLibrary: React.FC<RigPartsAndConfigurationProps> = ({
  switchToView,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<RigDetailsFormState>();
  const { data: searchResult, isFetching: implementsFetching } = useQueryPopups(
    atkEquipmentApi.endpoints.searchImplements.useQuery({
      customerId: values.customerId,
    })
  );

  return (
    <LibraryComponent
      isLoading={implementsFetching}
      entities={searchResult?.implements ?? []}
      dataPath={S.implements.$path}
      renderCard={(implement, arrayHelpers) => (
        <EquipmentLibraryCard
          key={implement.id}
          data={getImplementLibraryCardData(t, implement)}
          variant="small"
          onCardClick={() => {
            arrayHelpers.push(implement);
            switchToView("status-and-config");
          }}
        />
      )}
      close={() => switchToView("status-and-config")}
    />
  );
};

interface RigPartsAndConfigurationProps {
  isNewRig: boolean;
  serial: string | undefined;
  view: RigDetailsPageView;
  switchToView: RigDetailsSwitchViewCallback;
  summary: RobotSummaryResponse | undefined;
}

export const RigPartsAndConfiguration: React.FC<
  RigPartsAndConfigurationProps
> = (props) => {
  const { isNewRig, serial, view, switchToView, summary } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dirty, isValid, values, submitForm } =
    useFormikContext<RigDetailsFormState>();
  const { validationResult } = useContext(RigValidationContext);
  const [setActiveRig] = useMutationPopups(
    portalApi.endpoints.setActiveAtkRig.useMutation()
  );
  const dispatch = useDispatch();

  let name: string = "";
  if (summary) {
    name = valueOrLoading(summary.robot?.customerSerial, t);
  }

  const actionButtons: React.ComponentProps<
    typeof RigDetailsHeader
  >["actionButtons"] = [];
  const alreadyActive = summary?.robot?.activeAtkRigId === values.id;

  if (!isNewRig) {
    // only display some buttons for existing rigs, since they don't make sense
    // for a rig that hasn't been saved yet
    actionButtons.push(
      {
        color: "secondary",
        startIcon: <CopyIcon />,
        text: t("utils.actions.copy"),
        onClick: () =>
          navigate(getNewRigPath(values.serial), {
            state: {
              initialRig: saveFormStateToRig(values),
            } satisfies RigDetailsPageLocationState,
          }),
      },
      {
        startIcon: <ActiveIcon />,
        text: t("utils.actions.activate"),
        disabled: dirty || alreadyActive,
        disabledTooltip: t(
          "views.autotractor.rigDetails.activateDisabledUnsavedChanges"
        ),
        onClick: async () => {
          if (serial) {
            await setActiveRig({
              serial,
              rigId: values.id,
            });
            dispatch(
              addNotification({
                message: t(
                  "views.autotractor.switchRig.activatedNotification",
                  {
                    rigName: values.name,
                  }
                ),
                variant: "success",
              })
            );
          }
        },
      }
    );
  }

  const saveDisabledReasons: string[] = [];

  if (!dirty) {
    saveDisabledReasons.push(
      t("views.autotractor.rigDetails.saveDisabledTooltips.clean")
    );
  }
  if (dirty && !isValid && !values.name) {
    saveDisabledReasons.push(
      t(
        "views.autotractor.rigDetails.saveDisabledTooltips.nameValidationFailed"
      )
    );
  }
  if (
    dirty &&
    ((isNewRig && (!validationResult || validationResult.errors.length > 0)) ||
      (!isValid && Boolean(values.name)))
  ) {
    saveDisabledReasons.push(
      t(
        "views.autotractor.rigDetails.saveDisabledTooltips.newPrevalidationFailed"
      )
    );
  }

  const saveDisabled = saveDisabledReasons.length > 0;
  let saveDisabledTooltip: React.ReactNode | undefined;
  if (saveDisabledReasons.length > 1) {
    saveDisabledTooltip = (
      <ul className="list-disc pl-4 m-0">
        {saveDisabledReasons.map((reason, i) => (
          <li key={i}>{reason}</li>
        ))}
      </ul>
    );
  } else if (saveDisabledReasons.length === 1) {
    saveDisabledTooltip = saveDisabledReasons[0];
  }

  actionButtons.push({
    startIcon: <SaveIcon />,
    text: t("utils.actions.saveChanges"),
    disabled: saveDisabled,
    disabledTooltip: saveDisabledTooltip,
    onClick: submitForm,
  });

  let body: JSX.Element | undefined;

  switch (view) {
    case "status-and-config": {
      body = <StatusAndConfiguration readOnly={!editingSupported(values)} />;
      break;
    }
    case "tractor-library": {
      body = <TractorLibrary {...props} />;
      break;
    }
    case "implement-library": {
      body = <ImplementLibrary {...props} />;
      break;
    }
  }

  const editingUnsupportedVersion = !editingSupported(values);

  return (
    <>
      <Header
        hideTitle={true}
        title={name}
        parentLink={serial ? getSwitchRigPath(serial) : undefined}
      />
      <Page>
        <div className="flex flex-col gap-8">
          {editingUnsupportedVersion && (
            <Alert icon={<LockIcon fontSize="inherit" />} severity="warning">
              <AlertTitle>
                {t(
                  "views.autotractor.equipment.unsupportedVersion.rigLockedTitle"
                )}
              </AlertTitle>
              {t(
                "views.autotractor.equipment.unsupportedVersion.rigLockedBody"
              )}
            </Alert>
          )}
          <RigDetailsHeader
            breadcrumbs={[
              {
                text: valueOrLoading(summary?.robot?.customerSerial, t),
                onClick: () => navigate(getRobotPath(values.serial)),
              },
            ]}
            headline={values.name}
            editHeadlineFormValuePath={S.name.$path}
            actionButtons={actionButtons}
            readOnly={editingUnsupportedVersion}
          />
          <RigDiagram canEdit={isNewRig} switchToView={switchToView} />
          <hr className="w-full" />
          {body}
        </div>
      </Page>
    </>
  );
};
